import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon, XPToast, Toast } from '../components/shared';
import { Header } from '../components/layout';
import { RecipeFormModal, AddToPlanModal } from '../components/recipes';
import { LogDetailModal } from '../components/schedule';
import { AuthModal } from '../components/auth/AuthModal';
import { useAuth } from '../components/auth/AuthProvider';
import { useAppData } from '../hooks';
import { dataService } from '@foodbook/core-logic';
import { Recipe, MealItem } from '../types';
import { errorHandler } from '@foodbook/core-logic';
import { AppRouter } from '../routes';

const getRecipeRatingValue = (rating: number | { value: number; ratedAt?: string } | undefined) =>
  typeof rating === 'number' ? rating : rating?.value;

const getRecipeRatingDate = (rating: number | { value: number; ratedAt?: string } | undefined) =>
  typeof rating === 'number' ? undefined : rating?.ratedAt;

const updateAggregateRating = (
  currentAverage: number | undefined,
  currentCount: number | undefined,
  previousValue: number | undefined,
  nextValue: number
) => {
  const safeAverage = currentAverage ?? 0;
  const safeCount = currentCount ?? 0;

  if (previousValue && safeCount > 0) {
    const total = safeAverage * safeCount - previousValue + nextValue;
    return {
      average: Number((total / safeCount).toFixed(1)),
      count: safeCount,
    };
  }

  const nextCount = safeCount + 1;
  const total = safeAverage * safeCount + nextValue;
  return {
    average: Number((total / nextCount).toFixed(1)),
    count: nextCount,
  };
};

const removeAggregateRating = (
  currentAverage: number | undefined,
  currentCount: number | undefined,
  previousValue: number | undefined
) => {
  const safeAverage = currentAverage ?? 0;
  const safeCount = currentCount ?? 0;

  if (!previousValue || safeCount <= 0) {
    return {
      average: 0,
      count: 0,
    };
  }

  if (safeCount === 1) {
    return {
      average: 0,
      count: 0,
    };
  }

  const total = safeAverage * safeCount - previousValue;
  const nextCount = safeCount - 1;
  return {
    average: Number((total / nextCount).toFixed(1)),
    count: nextCount,
  };
};

/**
 * AppContainer 组件
 * 应用主容器，包含所有的业务逻辑和状态管理
 *
 * 职责：
 * 1. 管理全局状态（认证、模态框等）
 * 2. 处理路由相关的逻辑
 * 3. 集成 useAppData Hook
 * 4. 管理模态框的显示/隐藏
 */
export function AppContainer() {
  // 认证状态
  const { user: authUser, isAuthenticated, isLoading: isAuthLoading, login, logout: logoutUser } = useAuth();
  const navigate = useNavigate();

  // UI 状态
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [showLogDetail, setShowLogDetail] = useState(false);

  // App data hook
  const {
    scheduleByDate,
    currentSchedule,
    selectedDate,
    setSelectedDate,
    recipes,
    ingredients,
    shoppingList,
    user,
    setUser,
    inventory,
    logs,
    setLogs,
    showXP,
    xpAmount,
    showToast,
    toastMessage,
    showPlanModal,
    selectedRecipeForPlan,
    setShowPlanModal,
    setSelectedRecipeForPlan,
    awardXP,
    showToastMessage,
    handleMealItemClick,
    handleMealComplete,
    handleAddDishStart,
    handleRecipeSelect,
    handleAddToShoppingList,
    handleAddToPlanStart,
    handleAddToPlanConfirm,
    handleFindRecipe,
    handleCheckout,
    handleToggleTheme,
    handleToggleFavorite,
    handleToggleIngredientFavorite,
    handleSaveBatch,
    setIngredients,
    setShoppingList,
    setRecipes,
    handleCopySchedule,
    handleDeleteSchedule,
    handleDeleteMealItem,
    handleUpdateMealItem,
    handleGenerateShoppingListFromSchedule,
    getScheduleStats,
    handleSaveMealTimeConfigs,
    handleAddMealTimeConfig,
    handleUpdateMealTimeConfig,
    handleDeleteMealTimeConfig,
    customMealTimes,
    // 用户目标更新方法
    updateNutritionGoals,
    updateBodyData,
    updateDietaryPreferences,
    updateBasicInfo,
  } = useAppData(authUser, !isAuthLoading);

  // Handle save recipe
  const handleSaveRecipe = async (newRecipe: Recipe) => {
    try {
      if (selectedRecipeForPlan) {
        await dataService.updateUserRecipe(selectedRecipeForPlan.id, newRecipe);
        setRecipes(prev => prev.map(r => r.id === selectedRecipeForPlan.id ? { ...r, ...newRecipe, id: selectedRecipeForPlan.id } : r));
        showToastMessage(`食谱 "${newRecipe.name}" 已更新！`);
      } else {
        const createdRecipe = await dataService.createUserRecipe(newRecipe as any);
        setRecipes(prev => [...prev, createdRecipe as Recipe]);
        showToastMessage(`食谱 "${newRecipe.name}" 已保存！`);
      }
      setShowRecipeForm(false);
      setSelectedRecipeForPlan(null);
      awardXP(20);
    } catch (error) {
      const appError = errorHandler.handleError(error, { operation: 'saveRecipe', recipeId: selectedRecipeForPlan?.id });
      showToastMessage(errorHandler.getUserMessage(appError));
    }
  };

  // Wrapped handleFindRecipe for single argument usage
  const handleFindRecipeWrapped = (ingredientName?: string) => {
    handleFindRecipe(ingredientName || '', navigate);
  };

  // AI generate handlers
  const handleAIGenerateForSchedule = async (recipe: Recipe, mealTitle: string) => {
    try {
      const newMealItem: MealItem = {
        id: `ai-${Date.now()}`,
        name: recipe.name,
        calories: recipe.calories,
        completed: false,
        icon: 'restaurant',
        iconColorClass: 'text-wood-dark',
        recipeId: recipe.id,
        nutrition: recipe.nutrition || {
          calories: recipe.calories,
          protein: 0,
          fat: 0,
          carbs: 0,
          fiber: 0,
        },
      };

      const updatedSchedule = currentSchedule.map(meal => {
        if (meal.title === mealTitle) {
          return { ...meal, items: [...meal.items, newMealItem] };
        }
        return meal;
      });

      await dataService.saveScheduleByDate({ [selectedDate]: updatedSchedule });
      showToastMessage(`AI生成的食谱 "${recipe.name}" 已添加到${mealTitle}！`);
      awardXP(15);
    } catch (error) {
      showToastMessage(`添加失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  const handleAIGenerateForIngredients = async (recipe: Recipe) => {
    showToastMessage(`AI生成的食谱 "${recipe.name}" 已保存！`);
    navigate('/recipes');
    awardXP(15);
  };

  // Generate shopping list from current plan
  const handleGenerateShoppingListFromPlan = () => {
    const itemsToAdd: { name: string; quantity: string; linkedIngredientId?: string }[] = [];

    currentSchedule.forEach(meal => {
      meal.items.forEach(item => {
        if (item.recipeId) {
          const recipe = recipes.find(r => r.id === item.recipeId);
          if (recipe?.ingredients) {
            recipe.ingredients.forEach(ing => {
              const amountMatch = ing.amount.match(/(\d+)/);
              const requiredAmount = amountMatch ? parseInt(amountMatch[1]) : 0;
              const stockItem = inventory[ing.linkedIngredientId || ''];
              const currentStock = stockItem?.currentAmount || 0;

              if (currentStock < requiredAmount) {
                const shortage = requiredAmount - currentStock;
                const ingredient = ingredients.find(i => i.id === ing.linkedIngredientId);
                const unit = ingredient?.unit || stockItem?.unit || ing.amount.replace(/\d+/g, '').trim() || 'g';
                itemsToAdd.push({ name: ing.name, quantity: `${shortage}${unit}`, linkedIngredientId: ing.linkedIngredientId });
              }
            });
          }
        }
      });
    });

    if (itemsToAdd.length === 0) {
      showToastMessage('所有食材库存充足，无需添加购物清单');
      return;
    }

    const mergedItems = new Map<string, typeof itemsToAdd[0]>();
    itemsToAdd.forEach(item => {
      if (!mergedItems.has(item.name)) mergedItems.set(item.name, item);
    });

    const newItems = Array.from(mergedItems.values()).map(item => ({
      id: `s-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: item.name,
      quantity: item.quantity,
      checked: false,
      date: selectedDate,
      linkedIngredientId: item.linkedIngredientId,
    }));

    setShoppingList(prev => [...prev, ...newItems]);
    showToastMessage(`已添加 ${newItems.length} 项到购物清单`);
    awardXP(10);
  };

  // Navigate to recipe detail
  const handleNavigateToRecipeDetail = (recipeId: string) => {
    navigate(`/recipe/${recipeId}`);
  };

  const handleApplyCalorieSuggestion = async (recipeId: string, calories: number) => {
    try {
      const recipe = recipes.find(r => r.id === recipeId);
      if (!recipe) {
        showToastMessage('未找到对应食谱');
        return;
      }
      if (recipe.ownerId !== user.id) {
        showToastMessage('当前只能同步你自己的食谱热量');
        return;
      }

      const updates = {
        ...recipe,
        manualCalories: calories,
        calories,
      };
      await dataService.updateUserRecipe(recipeId, updates as any);
      setRecipes(prev => prev.map(r => r.id === recipeId ? { ...r, manualCalories: calories, calories } : r));
      showToastMessage(`已将《${recipe.name}》的热量同步为 ${calories} kcal`);
    } catch (error) {
      showToastMessage(`同步失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  const handleRateRecipe = async (recipeId: string, type: 'difficulty' | 'love', value: number) => {
    if (!isAuthenticated || !authUser) {
      showToastMessage('请先登录后再评分');
      return;
    }

    const recipe = recipes.find(item => item.id === recipeId);
    if (!recipe) {
      showToastMessage('未找到对应食谱');
      return;
    }

    const previousRatings = user.recipeRatings || {};
    const previousValue = getRecipeRatingValue(previousRatings[recipeId]?.[type]);
    const averageKey = type === 'difficulty' ? 'difficultyRatingAverage' : 'loveRatingAverage';
    const countKey = type === 'difficulty' ? 'difficultyRatingCount' : 'loveRatingCount';

    const nextAggregate = updateAggregateRating(
      recipe[averageKey],
      recipe[countKey],
      previousValue,
      value,
    );

    const updatedRecipe = {
      ...recipe,
      [averageKey]: nextAggregate.average,
      [countKey]: nextAggregate.count,
    };

    const updatedProfile = {
      ...user,
      recipeRatings: {
        ...(user.recipeRatings || {}),
        [recipeId]: {
          ...(user.recipeRatings?.[recipeId] || {}),
          [type]: {
            value,
            ratedAt: new Date().toISOString(),
          },
        },
      },
    };

    try {
      if (recipe.ownerId) {
        await dataService.updateUserRecipe(recipeId, updatedRecipe as any);
      } else {
        const publicRecipes = await dataService.getRecipes();
        const updatedPublicRecipes = publicRecipes.map((item) => item.id === recipeId ? { ...item, ...updatedRecipe } : item);
        await dataService.saveRecipes(updatedPublicRecipes);
      }

      await dataService.saveUserProfile(updatedProfile as any);
      setRecipes(prev => prev.map(item => item.id === recipeId ? { ...item, ...updatedRecipe } : item));
      setUser(updatedProfile as any);
      showToastMessage(type === 'difficulty' ? `已更新难度评分：⚡ ${value.toFixed(1)}` : `已更新喜爱评分：♥ ${value.toFixed(1)}`);
    } catch (error) {
      showToastMessage(`评分失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  const handleClearRecipeRating = async (recipeId: string, type: 'difficulty' | 'love') => {
    if (!isAuthenticated || !authUser) {
      showToastMessage('请先登录后再操作');
      return;
    }

    const recipe = recipes.find(item => item.id === recipeId);
    if (!recipe) {
      showToastMessage('未找到对应食谱');
      return;
    }

    const previousRatings = user.recipeRatings || {};
    const recipeRatingRecord = previousRatings[recipeId];
    const previousValue = getRecipeRatingValue(recipeRatingRecord?.[type]);
    if (!previousValue) {
      return;
    }

    const averageKey = type === 'difficulty' ? 'difficultyRatingAverage' : 'loveRatingAverage';
    const countKey = type === 'difficulty' ? 'difficultyRatingCount' : 'loveRatingCount';
    const nextAggregate = removeAggregateRating(
      recipe[averageKey],
      recipe[countKey],
      previousValue,
    );

    const updatedRecipe = {
      ...recipe,
      [averageKey]: nextAggregate.average || undefined,
      [countKey]: nextAggregate.count || undefined,
    };

    const nextRecipeRatingRecord = { ...(recipeRatingRecord || {}) };
    delete nextRecipeRatingRecord[type];

    const nextRecipeRatings = {
      ...(user.recipeRatings || {}),
    };

    if (Object.keys(nextRecipeRatingRecord).length > 0) {
      nextRecipeRatings[recipeId] = nextRecipeRatingRecord;
    } else {
      delete nextRecipeRatings[recipeId];
    }

    const updatedProfile = {
      ...user,
      recipeRatings: nextRecipeRatings,
    };

    try {
      if (recipe.ownerId) {
        await dataService.updateUserRecipe(recipeId, updatedRecipe as any);
      } else {
        const publicRecipes = await dataService.getRecipes();
        const updatedPublicRecipes = publicRecipes.map((item) => item.id === recipeId ? { ...item, ...updatedRecipe } : item);
        await dataService.saveRecipes(updatedPublicRecipes);
      }

      await dataService.saveUserProfile(updatedProfile as any);
      setRecipes(prev => prev.map(item => item.id === recipeId ? { ...item, ...updatedRecipe } : item));
      setUser(updatedProfile as any);
      showToastMessage(type === 'difficulty' ? '已取消难度评分' : '已取消喜爱评分');
    } catch (error) {
      showToastMessage(`取消评分失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  // Navigate to ingredients with optional ingredientId
  const handleNavigateToIngredient = (ingredientId?: string) => {
    if (ingredientId) {
      navigate(`/ingredients?highlight=${ingredientId}`);
    } else {
      navigate('/ingredients');
    }
  };

  // Recipe Detail Route Wrapper
  const { recipeId } = useParams<{ recipeId: string }>();

  return (
    <div className="flex min-h-[100dvh] flex-col overflow-hidden bg-[#fdf6e3] font-sans text-[#3e2723] transition-colors duration-300 selection:bg-[#ffb74d] selection:text-white dark:bg-[#2c2a25] md:h-screen">
      <Header
        authUser={authUser}
        isAuthenticated={isAuthenticated}
        onLogout={logoutUser}
        onShowAuthModal={() => setShowAuthModal(true)}
      />

      <main className="relative min-h-0 flex-1 overflow-hidden">
        <div className="page-container h-full min-h-0 overflow-hidden px-3 pt-3 pb-[calc(7.5rem+env(safe-area-inset-bottom))] md:px-10 md:py-6 md:pb-6">
          {isAuthLoading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <div className="jagged-corners border-2 border-[#c38b4d] bg-[#fff7e6] px-5 py-3 text-sm font-black text-[#7b4d1a] shadow-pixel-sm">
                正在恢复登录状态...
              </div>
            </div>
          ) : (
            <AppRouter
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              currentSchedule={currentSchedule}
              scheduleByDate={scheduleByDate}
              recipes={recipes}
              ingredients={ingredients}
              shoppingList={shoppingList}
              user={user}
              inventory={inventory}
              logs={logs}
              setLogs={setLogs as React.Dispatch<React.SetStateAction<typeof logs>>}
              customMealTimes={customMealTimes}
              isAuthenticated={isAuthenticated}
              onThemeToggle={handleToggleTheme}
              onLogout={logoutUser}
              onFindRecipe={handleFindRecipeWrapped}
              onViewLog={() => setShowLogDetail(true)}
              onOpenLogCenter={() => navigate('/log-history')}
              onCopySchedule={handleCopySchedule}
              onDeleteSchedule={handleDeleteSchedule}
              onGenerateShoppingListFromSchedule={handleGenerateShoppingListFromSchedule}
              onMealItemClick={handleMealItemClick}
              onMealComplete={handleMealComplete}
              onAddDish={handleAddDishStart}
              onDeleteMealItem={handleDeleteMealItem}
              onUpdateMealItem={handleUpdateMealItem}
              onViewRecipe={handleNavigateToRecipeDetail}
              onApplyCalorieSuggestion={handleApplyCalorieSuggestion}
              onAIGenerate={handleAIGenerateForSchedule}
              getScheduleStats={getScheduleStats}
              onSaveMealTimeConfigs={handleSaveMealTimeConfigs}
              onAddMealTimeConfig={handleAddMealTimeConfig}
              onUpdateMealTimeConfig={handleUpdateMealTimeConfig}
              onDeleteMealTimeConfig={handleDeleteMealTimeConfig}
              onGenerateShoppingListFromPlan={handleGenerateShoppingListFromPlan}
              recipeId={recipeId}
              onCloseRecipeDetail={() => navigate(-1)}
              onAddToShoppingList={handleAddToShoppingList}
              onAddToPlan={handleAddToPlanStart}
              onToggleFavorite={handleToggleFavorite}
              onToggleIngredientFavorite={handleToggleIngredientFavorite}
              onRateRecipe={handleRateRecipe}
              onClearRecipeRating={handleClearRecipeRating}
              onSaveBatch={handleSaveBatch}
              onCheckout={handleCheckout}
              onNavigateToIngredient={handleNavigateToIngredient}
              onNewRecipe={() => {
                setSelectedRecipeForPlan(null);
                setShowRecipeForm(true);
              }}
              onEditRecipe={(recipe) => {
                setSelectedRecipeForPlan(recipe);
                setShowRecipeForm(true);
              }}
              setRecipes={setRecipes}
              setIngredients={setIngredients}
              setShoppingList={setShoppingList}
              showToastMessage={showToastMessage}
              awardXP={awardXP}
              // 用户目标更新方法
              updateNutritionGoals={updateNutritionGoals}
              updateBodyData={updateBodyData}
              updateDietaryPreferences={updateDietaryPreferences}
              updateBasicInfo={updateBasicInfo}
            />
          )}

          {/* Modals & Overlays */}
          <XPToast show={showXP} amount={xpAmount} />
          <Toast show={showToast} message={toastMessage} />

          {showPlanModal && (
            <AddToPlanModal
              onClose={() => {}}
              onConfirm={handleAddToPlanConfirm}
            />
          )}

          {showRecipeForm && (
            <RecipeFormModal
              isOpen={showRecipeForm}
              onClose={() => {
                setShowRecipeForm(false);
                setSelectedRecipeForPlan(null);
              }}
              onSave={handleSaveRecipe}
              ingredients={ingredients}
              userInventory={inventory}
              editingRecipe={selectedRecipeForPlan as any}
            />
          )}

          <LogDetailModal
            isOpen={showLogDetail}
            onClose={() => setShowLogDetail(false)}
            expiringIngredients={ingredients
              .filter(ing => inventory[ing.id]?.batches.some(b => b.daysLeft < 3))
              .map(ing => ({ ...ing, batches: inventory[ing.id]?.batches || [] }))}
            onFindRecipe={(name) => handleFindRecipe(name, navigate)}
            logs={logs}
            onOpenLogCenter={() => navigate('/log-history')}
          />

          {/* Auth Modal */}
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onLoginSuccess={login}
            showToastMessage={showToastMessage}
          />
        </div>
      </main>
    </div>
  );
}
